import { BufferGeometry } from "three";
import { toCreasedNormals } from "three/examples/jsm/utils/BufferGeometryUtils.js";
import {
  insetConvexPolygon,
  LED_CONTOUR_INSET,
  makeFrameGeometry,
  makePolygonLedTubes,
  makePolygonShapeGeometry,
  polygonLedPointPositions,
  type AperturePolygon,
  type LedPointSample,
} from "./geometry";
import {
  makePerimeterArcWallGeometry,
  makePerimeterRailGeometry,
  makePerimeterWallGeometry,
  type PerimeterArcRange,
} from "./wallTextGeometry";
import type { FrameShape } from "./types";

/**
 * Session-level LRU caches for the heavy procedural geometries the
 * configurator produces every time `state.shape` / `state.size` change:
 *
 *   - `makeFrameGeometry` builds an `ExtrudeGeometry` (outer + inner ring,
 *     ~64 curve-segments). Combined with `toCreasedNormals` it costs
 *     ~30–80 ms on a mid-range laptop. The user routinely toggles between
 *     a handful of presets (e.g. 24" / 36" / 48" × square / circle), so
 *     >90 % of those rebuild calls are for shapes we have already built
 *     this session.
 *   - `makePolygonShapeGeometry` is cheaper but called twice per
 *     `worldHull` change (back-plane + glass plane) and the inputs
 *     (hull + insetScale) form a small finite set.
 *
 * Both caches are bounded LRU. Evicted entries call `dispose()` so the
 * GPU memory is freed when the user keeps cycling through fresh sizes.
 *
 * Float keys are rounded to 1e-4 m precision (≈ 0.1 mm) which is well
 * below mirror manufacturing tolerance — there is no benefit to caching
 * sub-millimetre variants separately.
 */

const MAX_FRAME_ENTRIES = 16;
const MAX_POLYGON_ENTRIES = 24;
/** Bar-tube and LED-point caches store one entry per `(hull, scale)` × layer.
 *  At depthLayers=11 the user can move through ~3 sizes / shapes before the
 *  cache evicts (33 entries). 48 keeps a comfortable working set. */
const MAX_BAR_TUBE_ENTRIES = 48;
const MAX_LED_POINT_ENTRIES = 96;
/** Perimeter wall/rail caches: one entry per (hull, scaleFront, scaleBack, zFront, zBack[, arcs])
 *  per band. With depthLayers up to 16 × 2 channels (long+short) × handful of
 *  shapes/sizes, a comfortable working set is ~96 walls + 48 rails. */
const MAX_PERIM_WALL_ENTRIES = 96;
const MAX_PERIM_RAIL_ENTRIES = 48;

class LRU<V extends { dispose(): void }> {
  private map = new Map<string, V>();
  constructor(private readonly capacity: number) {}

  get(key: string): V | undefined {
    const value = this.map.get(key);
    if (!value) return undefined;
    this.map.delete(key);
    this.map.set(key, value);
    return value;
  }

  set(key: string, value: V): void {
    if (this.map.has(key)) this.map.delete(key);
    this.map.set(key, value);
    while (this.map.size > this.capacity) {
      const oldestKey = this.map.keys().next().value;
      if (oldestKey === undefined) break;
      const oldest = this.map.get(oldestKey)!;
      this.map.delete(oldestKey);
      try {
        oldest.dispose();
      } catch {
        /* dispose can throw on already-disposed geometries — ignore. */
      }
    }
  }

  clear(): void {
    for (const value of this.map.values()) {
      try {
        value.dispose();
      } catch {
        /* ignore */
      }
    }
    this.map.clear();
  }
}

const frameCache = new LRU<BufferGeometry>(MAX_FRAME_ENTRIES);
const polygonShapeCache = new LRU<BufferGeometry>(MAX_POLYGON_ENTRIES);

/**
 * Bar-tube geometry list. `makePolygonLedTubes` returns `BufferGeometry[]`
 * so the disposable wrapper batches both the GPU buffer release and the
 * `Array<BufferGeometry>` GC for us.
 */
class BarTubeBundle {
  constructor(public readonly geometries: BufferGeometry[]) {}
  dispose(): void {
    for (const g of this.geometries) {
      try {
        g.dispose();
      } catch {
        /* ignore double dispose */
      }
    }
  }
}
const barTubeCache = new LRU<BarTubeBundle>(MAX_BAR_TUBE_ENTRIES);

/**
 * LED-point sample list. Pure data — no GPU resource — so the wrapper's
 * `dispose()` is a no-op. We still go through the same LRU class for
 * consistent eviction ordering with the geometry caches.
 */
class LedPointBundle {
  constructor(public readonly samples: LedPointSample[]) {}
  dispose(): void {
    /* no-op: pure JS data */
  }
}
const ledPointCache = new LRU<LedPointBundle>(MAX_LED_POINT_ENTRIES);
const perimeterWallCache = new LRU<BufferGeometry>(MAX_PERIM_WALL_ENTRIES);
const perimeterRailCache = new LRU<BufferGeometry>(MAX_PERIM_RAIL_ENTRIES);

function quantize(value: number): string {
  return value.toFixed(4);
}

/** Stable key for an arc list (rectangle/oval long-short channels). */
function arcsKey(arcs: PerimeterArcRange[] | null): string {
  if (!arcs || arcs.length === 0) return "all";
  let s = "";
  for (let i = 0; i < arcs.length; i++) {
    if (i > 0) s += ",";
    s += `${arcs[i].start}-${arcs[i].end}`;
  }
  return s;
}

/**
 * Extruded frame ring with creased normals, cached by `(shape, w, h)`.
 * Repeated requests for an identical signature return the SAME
 * `BufferGeometry` instance — multiple `<mesh>` nodes can share it
 * safely, three.js does not need exclusive ownership.
 */
export function getCachedFrameGeometry(
  shape: FrameShape,
  w: number,
  h: number,
): BufferGeometry {
  const key = `${shape}:${quantize(w)}:${quantize(h)}`;
  const cached = frameCache.get(key);
  if (cached) return cached;

  const raw = makeFrameGeometry(shape, w, h);
  const FRAME_CREASE_ANGLE = Math.PI / 3;
  const creased = toCreasedNormals(raw, FRAME_CREASE_ANGLE);
  if (creased !== raw) raw.dispose();
  frameCache.set(key, creased);
  return creased;
}

/**
 * Lookup-only sibling of `getCachedFrameGeometry`. Returns `null` on miss
 * **without** building — used by `ProcFrameMesh` to decide between an
 * instant render (cache hit) and a deferred async build (cache miss). The
 * cold cost of `makeFrameGeometry + toCreasedNormals` is ~30–80 ms on a
 * mid-range laptop and otherwise blocks the React render phase.
 */
export function peekFrameGeometry(
  shape: FrameShape,
  w: number,
  h: number,
): BufferGeometry | null {
  return frameCache.get(`${shape}:${quantize(w)}:${quantize(h)}`) ?? null;
}

/**
 * Hash key for an `AperturePolygon` — uses bounding-box dimensions plus
 * length, which is cheap and unique enough for our procedural hulls
 * (each hull comes from `buildApertureHull(shape, w, h)`, so once we
 * fix `(w, h, length)` the vertices match exactly).
 */
function hullKey(hull: AperturePolygon): string {
  if (hull.length === 0) return "0:0:0";
  let minX = hull[0].x;
  let maxX = hull[0].x;
  let minY = hull[0].y;
  let maxY = hull[0].y;
  for (let i = 1; i < hull.length; i++) {
    const p = hull[i];
    if (p.x < minX) minX = p.x;
    else if (p.x > maxX) maxX = p.x;
    if (p.y < minY) minY = p.y;
    else if (p.y > maxY) maxY = p.y;
  }
  return `${hull.length}:${quantize(maxX - minX)}:${quantize(maxY - minY)}`;
}

/**
 * Cached `makePolygonShapeGeometry`. The two callers in `MirrorScene`
 * (back plane at `scale = 1` and glass plane at `scale = GLASS_INSET`)
 * each end up with a tiny finite cardinality (one entry per
 * shape + size combo × per-scale).
 */
export function getCachedPolygonShapeGeometry(
  hull: AperturePolygon,
  insetScale: number,
): BufferGeometry {
  const key = `${hullKey(hull)}:${quantize(insetScale)}`;
  const cached = polygonShapeCache.get(key);
  if (cached) return cached;
  const geo = makePolygonShapeGeometry(hull, insetScale);
  polygonShapeCache.set(key, geo);
  return geo;
}

/**
 * Cached bar-tube geometry list keyed on `(hull, scale, pathZ)`. The
 * scaled/inset perimeter is rebuilt only the first time a layer's
 * triplet is seen — switching `lightStyle` away from "bar" and back
 * (or cycling depthLayers between 11 ↔ 13) reuses the same buffers.
 */
export function getCachedBarTubeGeometries(
  hull: AperturePolygon,
  scale: number,
  pathZ: number,
): BufferGeometry[] {
  const key = `${hullKey(hull)}:${quantize(scale)}:${quantize(pathZ)}`;
  const cached = barTubeCache.get(key);
  if (cached) return cached.geometries;
  const built = makePolygonLedTubes(hull, scale, pathZ);
  barTubeCache.set(key, new BarTubeBundle(built));
  return built;
}

/**
 * Cached LED-point samples keyed on `(hull, scale × layerScale, count)`.
 * `polygonLedPointPositions` walks the polygon perimeter and runs an
 * inset solve per layer — cheap individually, but called for every depth
 * layer on every shape change, which adds up.
 */
export function getCachedLedPointSamples(
  hull: AperturePolygon,
  scale: number,
  layerScale: number,
  count: number,
): LedPointSample[] {
  const key = `${hullKey(hull)}:${quantize(scale * layerScale)}:${count}`;
  const cached = ledPointCache.get(key);
  if (cached) return cached.samples;
  /* Match the inline pipeline previously living in `MirrorScene`'s
   * `ledPointLayers` memo: scale → inset by `LED_CONTOUR_INSET` → sample. */
  const layerHull = hull.map((p) => ({ x: p.x * layerScale, y: p.y * layerScale }));
  const inset = insetConvexPolygon(layerHull, 1, LED_CONTOUR_INSET);
  const target = inset.length >= 3 ? inset : layerHull;
  const samples = polygonLedPointPositions(target, 1, 1, count);
  ledPointCache.set(key, new LedPointBundle(samples));
  return samples;
}

/**
 * Cached cavity-wall ring (frustum strip) for perimeter text-lighting bands.
 * Geometry is independent of text content — only of band Z bounds + scale +
 * (optionally) arc range. Caching lets a `centerText` / `ledTextContent`
 * keystroke skip rebuilding ~depthLayers × channels worth of `BufferGeometry`
 * per re-bake.
 */
export function getCachedPerimeterWallGeometry(
  hull: AperturePolygon,
  arcs: PerimeterArcRange[] | null,
  scaleFront: number,
  scaleBack: number,
  zFront: number,
  zBack: number,
): BufferGeometry {
  const key = `${hullKey(hull)}:${arcsKey(arcs)}:${quantize(scaleFront)}:${quantize(scaleBack)}:${quantize(zFront)}:${quantize(zBack)}`;
  const cached = perimeterWallCache.get(key);
  if (cached) return cached;
  const built = arcs
    ? makePerimeterArcWallGeometry(hull, arcs, scaleFront, scaleBack, zFront, zBack)
    : makePerimeterWallGeometry(hull, scaleFront, scaleBack, zFront, zBack);
  perimeterWallCache.set(key, built);
  return built;
}

/**
 * Cached perimeter rail tube for LED rails along the cavity walls. Same
 * caching rationale as the wall — geometry depends only on hull + scale + Z +
 * radius, not on the text content that triggered the re-bake.
 */
export function getCachedPerimeterRailGeometry(
  hull: AperturePolygon,
  scale: number,
  z: number,
  radius: number,
): BufferGeometry {
  const key = `${hullKey(hull)}:${quantize(scale)}:${quantize(z)}:${quantize(radius)}`;
  const cached = perimeterRailCache.get(key);
  if (cached) return cached;
  const built = makePerimeterRailGeometry(hull, scale, z, radius);
  perimeterRailCache.set(key, built);
  return built;
}

/** Drop every cached geometry. Wire to `MirrorGroup` unmount only — the
 *  user-visible session ends there and we want the GPU memory back. */
export function clearGeometryCache(): void {
  frameCache.clear();
  polygonShapeCache.clear();
  barTubeCache.clear();
  ledPointCache.clear();
  perimeterWallCache.clear();
  perimeterRailCache.clear();
}
